import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Subscription, interval, timer } from 'rxjs';
import { catchError, of, switchMap, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';

export type ConnectivityStatus = 'online' | 'offline' | 'checking';

/**
 * `navigator.onLine` détecte uniquement la présence d'une interface réseau,
 * pas un accès réel à l'API (portails captifs, Wi-Fi sans internet, etc.).
 * On fiabilise donc l'état avec un ping périodique sur /api/health
 * (App\Controller\HealthController côté backend), tout en réagissant
 * immédiatement aux événements online/offline du navigateur.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService implements OnDestroy {
  private readonly http = inject(HttpClient);

  private readonly statusSignal = signal<ConnectivityStatus>(
    navigator.onLine ? 'checking' : 'offline',
  );
  readonly status = this.statusSignal.asReadonly();

  private readonly onlineHandler = () => this.checkNow();
  private readonly offlineHandler = () => this.statusSignal.set('offline');
  private pollSub?: Subscription;

  constructor() {
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);

    // Vérifie l'accès réel à l'API toutes les 20s, et immédiatement au démarrage.
    this.pollSub = interval(20_000).subscribe(() => this.checkNow());
    this.checkNow();
  }

  checkNow(): void {
    if (!navigator.onLine) {
      this.statusSignal.set('offline');
      return;
    }

    timer(0)
      .pipe(
        switchMap(() =>
          this.http.get(`${environment.apiUrl}/health`, { responseType: 'text' }).pipe(
            timeout(4000),
            catchError(() => of(null)),
          ),
        ),
      )
      .subscribe((result) => {
        this.statusSignal.set(result !== null ? 'online' : 'offline');
      });
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
    this.pollSub?.unsubscribe();
  }
}
